import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

export type SelectOption<V extends string = string> = {
  value: V;
  label: string;
  description?: string;
  /** Short text shown on the right, e.g. a code or count. */
  badge?: string;
  /** Leading visual (code chip, dot, icon). */
  prefix?: ReactNode;
  disabled?: boolean;
  /** Extra text matched by search but not displayed. */
  keywords?: string;
};

type SelectProps<V extends string> = {
  value: string | null | undefined;
  onChange: (value: V) => void;
  options: SelectOption<V>[];
  placeholder?: string;
  /** Defaults to on when there are more than 7 options. */
  searchable?: boolean;
  searchPlaceholder?: string;
  clearable?: boolean;
  onClear?: () => void;
  disabled?: boolean;
  emptyText?: string;
  className?: string;
  id?: string;
  size?: 'sm' | 'md';
};

function Chevron({ open }: { open: boolean }) {
  return (
    <svg className={`h-4 w-4 shrink-0 text-[#b77c98] transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="none" aria-hidden>
      <path d="m5.5 7.5 4.5 4.5 4.5-4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Check() {
  return (
    <svg className="h-4 w-4 shrink-0 text-[#d6006e]" viewBox="0 0 20 20" fill="none" aria-hidden>
      <path d="m5 10.5 3.2 3.2L15 7" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function Select<V extends string = string>({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  searchable,
  searchPlaceholder = 'Search…',
  clearable,
  onClear,
  disabled,
  emptyText = 'No matches',
  className = '',
  id,
  size = 'md',
}: SelectProps<V>) {
  const autoId = useId();
  const listId = `${id || autoId}-list`;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [dropUp, setDropUp] = useState(false);
  const typeahead = useRef({ text: '', at: 0 });

  const canSearch = searchable ?? options.length > 7;
  const selected = options.find((o) => o.value === value) || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      [o.label, o.description, o.badge, o.value, o.keywords].some((t) => t && t.toLowerCase().includes(q)),
    );
  }, [options, query]);

  const close = (focusTrigger = true) => {
    setOpen(false);
    setQuery('');
    if (focusTrigger) triggerRef.current?.focus();
  };

  const openList = () => {
    if (disabled) return;
    const idx = Math.max(0, options.findIndex((o) => o.value === value));
    setActive(idx);
    setOpen(true);
  };

  const pick = (opt: SelectOption<V> | undefined) => {
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    close();
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropUp(window.innerHeight - rect.bottom < 300 && rect.top > 320);
    if (canSearch) searchRef.current?.focus();
    else listRef.current?.focus();
  }, [open, canSearch]);

  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const move = (delta: number) => {
    if (!filtered.length) return;
    let next = active;
    for (let i = 0; i < filtered.length; i += 1) {
      next = (next + delta + filtered.length) % filtered.length;
      if (!filtered[next].disabled) break;
    }
    setActive(next);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(filtered.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(filtered[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Tab') {
      close(false);
    } else if (!canSearch && e.key.length === 1) {
      const now = Date.now();
      typeahead.current.text = now - typeahead.current.at < 700 ? typeahead.current.text + e.key.toLowerCase() : e.key.toLowerCase();
      typeahead.current.at = now;
      const idx = filtered.findIndex((o) => o.label.toLowerCase().startsWith(typeahead.current.text));
      if (idx >= 0) setActive(idx);
    }
  };

  const pad = size === 'sm' ? 'py-1.5 text-[13px]' : 'py-2 text-sm';

  return (
    <div ref={rootRef} className={`relative ${className}`} onKeyDown={onKeyDown}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        disabled={disabled}
        onClick={() => (open ? close() : openList())}
        className={`mz-input flex items-center gap-2 text-left ${pad} ${open ? '!border-[#d6006e] shadow-[0_0_0_3px_rgba(214,0,110,0.12)]' : ''}`}
      >
        {selected?.prefix}
        <span className={`min-w-0 flex-1 truncate ${selected ? 'text-[#3b0a22]' : 'text-[#c9a3b5]'}`}>{selected ? selected.label : placeholder}</span>
        {selected?.badge && <span className="shrink-0 text-[11px] font-semibold text-[#b77c98]">{selected.badge}</span>}
        {clearable && selected && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Clear selection"
            onClick={(e) => {
              e.stopPropagation();
              onClear?.();
            }}
            className="-mr-1 rounded p-0.5 text-[#b77c98] hover:bg-[#fdf2f8] hover:text-[#d6006e]"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" aria-hidden>
              <path d="m6 6 8 8M14 6l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </span>
        )}
        <Chevron open={open} />
      </button>

      {open && (
        <div
          className={`mz-pop absolute left-0 right-0 z-50 min-w-[220px] overflow-hidden rounded-xl bg-white ${dropUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5'}`}
          style={{ border: '1px solid #f0cfdd', boxShadow: '0 18px 44px rgba(59,10,34,0.16)' }}
        >
          {canSearch && (
            <div className="border-b border-[#fbeef4] p-2">
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
                placeholder={searchPlaceholder}
                className="w-full rounded-md bg-[#fdf6f9] px-2.5 py-1.5 text-[13px] text-[#3b0a22] outline-none placeholder:text-[#c9a3b5]"
                aria-controls={listId}
                aria-activedescendant={filtered[active] ? `${listId}-${active}` : undefined}
              />
            </div>
          )}
          <ul ref={listRef} id={listId} role="listbox" tabIndex={-1} className="max-h-64 overflow-y-auto p-1 outline-none">
            {filtered.length === 0 && <li className="px-3 py-6 text-center text-[13px] text-[#b77c98]">{emptyText}</li>}
            {filtered.map((opt, i) => {
              const isSel = opt.value === value;
              const isActive = i === active;
              return (
                <li
                  key={opt.value}
                  id={`${listId}-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={isSel}
                  aria-disabled={opt.disabled}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(opt)}
                  className={`flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 ${
                    opt.disabled ? 'cursor-not-allowed opacity-45' : isActive ? 'bg-[#fdf2f8]' : ''
                  }`}
                >
                  {opt.prefix}
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-[13px] ${isSel ? 'font-semibold text-[#d6006e]' : 'font-medium text-[#3b0a22]'}`}>{opt.label}</span>
                    {opt.description && <span className="block truncate text-[11.5px] text-[#a77d92]">{opt.description}</span>}
                  </span>
                  {opt.badge && <span className="shrink-0 text-[11px] font-semibold text-[#b77c98]">{opt.badge}</span>}
                  {isSel ? <Check /> : <span className="w-4 shrink-0" />}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
